import { useEffect, useState, useCallback, useMemo } from 'react';
import Sidebar from '../../../components/sidebar/Sidebar';
import '../ServerData.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:9090/api';
const URGENT_AFTER_DAYS = 7;
const DAY_MS = 86400000;

function getToken() {
  return localStorage.getItem('token');
}

function authHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` };
}

const STATUS_FILTERS = ['all', 'pending', 'approved', 'rejected', 'declined'];
const SORT_OPTIONS = [
  { value: 'date', label: 'Date Requested' },
  { value: 'urgency', label: 'Urgency' },
  { value: 'popularity', label: 'Popularity' },
];

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

function isUrgent(req) {
  if (req.status !== 'pending' || !req.submittedAt) return false;
  return (Date.now() - new Date(req.submittedAt).getTime()) > URGENT_AFTER_DAYS * DAY_MS;
}

function StatusBadge({ status }) {
  return <span className={`sd-badge sd-badge-${status}`}>{status}</span>;
}

const TEMPLATE_KINDS = ['approve', 'reject', 'any'];

// Drops a saved reply into the textarea below it. The text stays editable
// afterwards, so a template is a starting point rather than a fixed message.
function TemplatePicker({ options, onPick }) {
  if (!options.length) return null;
  return (
    <select
      className="sd-filter-btn"
      style={{ width: '100%', marginBottom: 8 }}
      value=""
      onChange={e => {
        const picked = options.find(t => String(t._id) === e.target.value);
        if (picked) onPick(picked.body);
      }}
    >
      <option value="">Insert a saved reply…</option>
      {options.map(t => (
        <option key={String(t._id)} value={String(t._id)}>{t.label}</option>
      ))}
    </select>
  );
}

function TemplateRow({ tpl, onChange, onError }) {
  const [label, setLabel] = useState(tpl.label);
  const [body, setBody]   = useState(tpl.body);
  const [kind, setKind]   = useState(tpl.kind);
  const [busy, setBusy]   = useState(false);

  const dirty = label !== tpl.label || body !== tpl.body || kind !== tpl.kind;

  const send = async (method, payload) => {
    setBusy(true);
    onError('');
    try {
      const res = await fetch(`${API}/reply-templates/${tpl._id}`, {
        method,
        headers: authHeaders(),
        body: payload ? JSON.stringify(payload) : undefined,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        onError(data.error || `Request failed (${res.status})`);
        return;
      }
      await onChange();
    } catch (err) {
      onError(`Couldn't reach the API: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sd-field-group" style={{ borderTop: '1px solid rgba(148,163,184,0.15)', paddingTop: 12 }}>
      <div className="sd-filters" style={{ marginBottom: 6 }}>
        <input
          className="sd-filter-btn"
          style={{ flex: 1, minWidth: 180 }}
          value={label}
          placeholder="Label"
          onChange={e => setLabel(e.target.value)}
        />
        <select className="sd-filter-btn" value={kind} onChange={e => setKind(e.target.value)}>
          {TEMPLATE_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <button
          className="sd-btn sd-btn-approve"
          disabled={busy || !dirty}
          onClick={() => send('PUT', { label, body, kind })}
        >
          <i className="bi bi-save" /> Save
        </button>
        <button
          className="sd-btn sd-btn-reject"
          disabled={busy}
          onClick={() => { if (window.confirm(`Delete template "${tpl.label}"?`)) send('DELETE'); }}
        >
          <i className="bi bi-trash" /> Delete
        </button>
      </div>
      <textarea className="sd-textarea" value={body} onChange={e => setBody(e.target.value)} />
    </div>
  );
}

// Templates live in Mongo so they can be edited here rather than in the source.
function TemplateManager({ templates, onChange }) {
  const [open, setOpen]   = useState(false);
  const [label, setLabel] = useState('');
  const [body, setBody]   = useState('');
  const [kind, setKind]   = useState('approve');
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');

  const add = async () => {
    if (!label.trim() || !body.trim()) {
      setError('Label and body are both required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API}/reply-templates`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ label, body, kind, sortOrder: templates.length * 10 }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || `Request failed (${res.status})`);
        return;
      }
      setLabel('');
      setBody('');
      await onChange();
    } catch (err) {
      setError(`Couldn't reach the API: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sd-card" style={{ marginBottom: 16 }}>
      <div className="sd-card-header" onClick={() => setOpen(o => !o)}>
        <div className="sd-card-left">
          <div className="sd-card-content">
            <i className="bi bi-chat-square-text" /> Manage reply templates
          </div>
          <div className="sd-card-meta">
            <span>{templates.length} saved</span>
          </div>
        </div>
        <div className="sd-card-actions">
          <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ color: '#475569', fontSize: '0.8rem' }} />
        </div>
      </div>

      {open && (
        <div className="sd-detail">
          {error && <div className="sd-field-value" style={{ color: '#f87171' }}>{error}</div>}

          {templates.map(t => (
            <TemplateRow key={String(t._id)} tpl={t} onChange={onChange} onError={setError} />
          ))}

          <div className="sd-field-group" style={{ borderTop: '1px solid rgba(148,163,184,0.15)', paddingTop: 12 }}>
            <div className="sd-field-label">Add a template</div>
            <div className="sd-filters" style={{ marginBottom: 6 }}>
              <input
                className="sd-filter-btn"
                style={{ flex: 1, minWidth: 180 }}
                value={label}
                placeholder="Label, e.g. Approved — added to queue"
                onChange={e => setLabel(e.target.value)}
              />
              <select className="sd-filter-btn" value={kind} onChange={e => setKind(e.target.value)}>
                {TEMPLATE_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
              <button className="sd-btn sd-btn-approve" disabled={busy} onClick={add}>
                <i className="bi bi-plus-circle" /> Add
              </button>
            </div>
            <textarea
              className="sd-textarea"
              placeholder="The message sent to the user…"
              value={body}
              onChange={e => setBody(e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function RequestCard({ req, onUpdate, templates }) {
  const [open, setOpen]             = useState(false);
  const [adminNote, setAdminNote]   = useState(req.adminNote || '');
  const [reason, setReason]         = useState(req.rejectionReason || '');
  const [saving, setSaving]         = useState(false);

  const respond = async (status) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/botdata/requests/${req._id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          status,
          adminNote,
          rejectionReason: status === 'rejected' ? reason : null,
          respondedBy: 'Dashboard',
        }),
      });
      if (res.ok) onUpdate();
    } finally {
      setSaving(false);
    }
  };

  const isPending = req.status === 'pending';
  const urgent = isUrgent(req);

  const approveTemplates = templates.filter(t => t.kind === 'approve' || t.kind === 'any');
  const rejectTemplates  = templates.filter(t => t.kind === 'reject'  || t.kind === 'any');

  return (
    <div className="sd-card" style={urgent ? { borderColor: 'rgba(239,68,68,0.4)' } : undefined}>
      <div className="sd-card-header" onClick={() => setOpen(o => !o)}>
        {req.posterUrl ? (
          <img src={req.posterUrl} alt="" className="sd-poster" />
        ) : (
          <div className="sd-poster sd-poster-placeholder"><i className="bi bi-film" /></div>
        )}

        <div className="sd-card-left">
          <div className="sd-card-meta">
            {req.botId && <span className="sd-bot-pill"><i className="bi bi-robot" />{req.botId}</span>}
            <span><i className="bi bi-person" /> {req.username}</span>
            <span><i className="bi bi-building" /> {req.guildName || req.guildId}</span>
            <span><i className="bi bi-clock" /> {formatDate(req.submittedAt)}</span>
            {req.contentType && <span><i className="bi bi-film" /> {req.contentType}</span>}
            {req.service    && <span><i className="bi bi-layers" /> {req.service}</span>}
            {urgent && <span className="sd-badge sd-badge-urgent"><i className="bi bi-exclamation-triangle-fill" /> Urgent</span>}
          </div>
          <div className="sd-card-content">{req.title || req.content}</div>
          {req.genres?.length > 0 && (
            <div className="sd-genre-row">
              {req.genres.map(g => <span key={g} className="sd-genre-chip">{g}</span>)}
            </div>
          )}
        </div>
        <div className="sd-card-actions">
          <StatusBadge status={req.status} />
          {req.replied && <span className="sd-badge sd-badge-resolved" title="Bot has replied">replied</span>}
          <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} style={{ color: '#475569', fontSize: '0.8rem' }} />
        </div>
      </div>

      {open && (
        <div className="sd-detail">
          {req.tmdbId && (
            <div className="sd-field-group">
              <div className="sd-field-label">TMDB ID</div>
              <div className="sd-field-value">
                {req.tmdbId} ({req.contentType}){req.voteAverage != null && ` · ★ ${req.voteAverage.toFixed(1)}`}
              </div>
            </div>
          )}
          {req.adminNote && (
            <div className="sd-field-group">
              <div className="sd-field-label">Admin Note</div>
              <div className="sd-field-value">{req.adminNote}</div>
            </div>
          )}
          {req.rejectionReason && (
            <div className="sd-field-group">
              <div className="sd-field-label">Rejection Reason</div>
              <div className="sd-field-value" style={{ color: '#f87171' }}>{req.rejectionReason}</div>
            </div>
          )}
          {req.respondedAt && (
            <div className="sd-field-group">
              <div className="sd-field-label">Responded</div>
              <div className="sd-field-value">{formatDate(req.respondedAt)} by {req.respondedBy || '—'}</div>
            </div>
          )}

          {isPending && (
            <>
              <div className="sd-field-group">
                <div className="sd-field-label">Admin Note (optional)</div>
                <TemplatePicker options={approveTemplates} onPick={setAdminNote} />
                <textarea
                  className="sd-textarea"
                  placeholder="Add a note visible in the Discord reply..."
                  value={adminNote}
                  onChange={e => setAdminNote(e.target.value)}
                />
              </div>
              <div className="sd-field-group">
                <div className="sd-field-label">Rejection Reason (if rejecting)</div>
                <TemplatePicker options={rejectTemplates} onPick={setReason} />
                <textarea
                  className="sd-textarea"
                  placeholder="Why is this being rejected?"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                />
              </div>
              <div className="sd-action-row">
                <button className="sd-btn sd-btn-approve" onClick={() => respond('approved')} disabled={saving}>
                  <i className="bi bi-check-circle" /> Approve
                </button>
                <button className="sd-btn sd-btn-reject" onClick={() => respond('rejected')} disabled={saving}>
                  <i className="bi bi-x-circle" /> Reject
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function BotRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [templates, setTemplates] = useState([]);
  const [filter, setFilter]     = useState('all');
  const [userFilter, setUserFilter]     = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [genreFilter, setGenreFilter]   = useState('');
  const [urgentOnly, setUrgentOnly]     = useState(false);
  const [sortBy, setSortBy]             = useState('date');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/botdata/requests`, { headers: authHeaders() });
      if (!res.ok) {
        setRequests([]);
        setError(`Couldn't load requests — the API returned ${res.status}.`);
        return;
      }
      const data = await res.json();
      if (!Array.isArray(data)) {
        setRequests([]);
        setError("Couldn't load requests — the API returned an unexpected response.");
        return;
      }
      setRequests(data);
    } catch (err) {
      setRequests([]);
      setError(`Couldn't reach the API: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API}/reply-templates`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data)) setTemplates(data);
    } catch {
      // Templates are a convenience; failing to load them must not block the page.
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const counts = {
    all:      requests.length,
    pending:  requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
    declined: requests.filter(r => r.status === 'declined').length,
  };
  const urgentCount = requests.filter(isUrgent).length;

  const distinctUsers    = useMemo(() => [...new Set(requests.map(r => r.username).filter(Boolean))].sort(), [requests]);
  const distinctServices = useMemo(() => [...new Set(requests.map(r => r.service).filter(Boolean))].sort(), [requests]);
  const distinctGenres   = useMemo(() => [...new Set(requests.flatMap(r => r.genres || []))].sort(), [requests]);

  const visibleRequests = useMemo(() => {
    const filtered = requests
      .filter(r => filter === 'all' || r.status === filter)
      .filter(r => !userFilter || r.username === userFilter)
      .filter(r => !serviceFilter || r.service === serviceFilter)
      .filter(r => !genreFilter || (r.genres || []).includes(genreFilter))
      .filter(r => !urgentOnly || isUrgent(r));

    return [...filtered].sort((a, b) => {
      if (sortBy === 'urgency') {
        const diff = Number(isUrgent(b)) - Number(isUrgent(a));
        if (diff !== 0) return diff;
      }
      if (sortBy === 'popularity') {
        return (b.popularity || 0) - (a.popularity || 0);
      }
      return new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0);
    });
  }, [requests, filter, userFilter, serviceFilter, genreFilter, urgentOnly, sortBy]);

  return (
    <div className="sd-page">
      <Sidebar title="Bot Requests" />

      <div className="sd-header">
        <div className="sd-title">
          <i className="bi bi-inbox-fill" />
          Bot Requests
        </div>
        <div className="sd-filters">
          {STATUS_FILTERS.map(f => (
            <button
              key={f}
              className={`sd-filter-btn${filter === f ? ' active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {counts[f] > 0 && <span style={{ marginLeft: 4, opacity: 0.7 }}>({counts[f]})</span>}
            </button>
          ))}
          <button
            className={`sd-filter-btn${urgentOnly ? ' active' : ''}`}
            onClick={() => setUrgentOnly(u => !u)}
            style={urgentOnly ? { borderColor: 'rgba(239,68,68,0.5)', color: '#f87171', background: 'rgba(239,68,68,0.1)' } : undefined}
          >
            <i className="bi bi-exclamation-triangle-fill" /> Urgent{urgentCount > 0 && ` (${urgentCount})`}
          </button>
          <button className="sd-filter-btn" onClick={load} title="Refresh">
            <i className="bi bi-arrow-clockwise" />
          </button>
        </div>
      </div>

      <div className="sd-filters" style={{ marginBottom: 16 }}>
        <select className="sd-filter-btn" value={userFilter} onChange={e => setUserFilter(e.target.value)}>
          <option value="">All users</option>
          {distinctUsers.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <select className="sd-filter-btn" value={serviceFilter} onChange={e => setServiceFilter(e.target.value)}>
          <option value="">All services</option>
          {distinctServices.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="sd-filter-btn" value={genreFilter} onChange={e => setGenreFilter(e.target.value)}>
          <option value="">All genres</option>
          {distinctGenres.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <select className="sd-filter-btn" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>Sort: {o.label}</option>)}
        </select>
      </div>

      <TemplateManager templates={templates} onChange={loadTemplates} />

      <div className="sd-stats">
        {[
          { label: 'Total', value: requests.length, color: '#e2e8f0' },
          { label: 'Pending', value: counts.pending, color: '#fbbf24' },
          { label: 'Approved', value: counts.approved, color: '#4ade80' },
          { label: 'Rejected', value: counts.rejected, color: '#f87171' },
          { label: 'Awaiting Reply', value: requests.filter(r => !r.replied && r.status !== 'pending').length, color: '#60a5fa' },
        ].map(s => (
          <div className="sd-stat-pill" key={s.label}>
            <div className="label">{s.label}</div>
            <div className="value" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {error && (
        <div className="sd-empty" style={{ borderColor: 'rgba(239,68,68,0.4)', color: '#f87171' }}>
          <i className="bi bi-exclamation-triangle-fill" />
          <p>{error}</p>
          <button className="sd-filter-btn" onClick={load} style={{ marginTop: 10 }}>
            <i className="bi bi-arrow-clockwise" /> Retry
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="sd-skel" style={{ height: 68, borderRadius: 14 }} />
          ))}
        </div>
      ) : error ? null : visibleRequests.length === 0 ? (
        <div className="sd-empty">
          <i className="bi bi-inbox" />
          <p>No requests found</p>
        </div>
      ) : (
        visibleRequests.map(req => (
          <RequestCard key={String(req._id)} req={req} onUpdate={load} templates={templates} />
        ))
      )}
    </div>
  );
}
